import { useCallback, useMemo, useState } from "react"

import { useGetProductInventoryPalletGroups } from "./useGetProductInventoryPalletGroups"

import type { InventoryPalletGroup, InventoryPalletLot } from "@/graphql/codegen/graphql"
import { InventoryPalletGroupsSortField, SortingOrder } from "@/graphql/codegen/graphql"
import useDebounce from "@/utils/useDebounce"

export interface SelectedLot {
  lot: InventoryPalletLot
  pallet: InventoryPalletGroup
}

interface UseInventoryAdjustmentPalletGroupSelectionParams {
  productExternalSystemId: string
  warehouseAreaExternalSystemId: string
}

interface UseInventoryAdjustmentPalletGroupSelectionReturn {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  selectedLotIds: Set<string>
  selectedLots: SelectedLot[]
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  handleSearchChange: (value: string) => void
  handleSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  handleSelectionChange: (lotIds: Set<string>) => void
  handleSelectionRemove: (lotId: string) => void
  handleSelectionReset: () => void
}

export const useInventoryAdjustmentPalletGroupSelection = ({
  productExternalSystemId,
  warehouseAreaExternalSystemId,
}: UseInventoryAdjustmentPalletGroupSelectionParams): UseInventoryAdjustmentPalletGroupSelectionReturn => {
  const [selectedLotIds, setSelectedLotIds] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState("")
  const [sortBy, setSortBy] = useState<InventoryPalletGroupsSortField>(InventoryPalletGroupsSortField.ExpirationDate)
  const [sortDirection, setSortDirection] = useState<SortingOrder>(SortingOrder.Asc)

  const debouncedSearch = useDebounce(search, 300)

  const { palletGroups, isFetching } = useGetProductInventoryPalletGroups({
    prodNbr: Number(productExternalSystemId),
    whsNbr: Number(warehouseAreaExternalSystemId),
    search: debouncedSearch || undefined,
    sortBy,
    sortDirection,
  })

  const selectedLots = useMemo(
    () =>
      palletGroups.flatMap((pallet) =>
        pallet.lots.filter((lot) => selectedLotIds.has(lot.rawInventoryId)).map((lot) => ({ lot, pallet }))
      ),
    [palletGroups, selectedLotIds]
  )

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
  }, [])

  const handleSortChange = useCallback((field: InventoryPalletGroupsSortField, direction: SortingOrder) => {
    setSortBy(field)
    setSortDirection(direction)
  }, [])

  const handleSelectionChange = useCallback((lotIds: Set<string>) => {
    setSelectedLotIds(lotIds)
  }, [])

  const handleSelectionRemove = useCallback((lotId: string) => {
    setSelectedLotIds((prev) => {
      const next = new Set(prev)
      next.delete(lotId)
      return next
    })
  }, [])

  const handleSelectionReset = useCallback(() => {
    setSelectedLotIds(new Set())
  }, [])

  return {
    palletGroups,
    isFetching,
    selectedLotIds,
    selectedLots,
    sortBy,
    sortDirection,
    handleSearchChange,
    handleSortChange,
    handleSelectionChange,
    handleSelectionRemove,
    handleSelectionReset,
  }
}
